/**
 * MATLAB Toolbox Package Definitions
 *
 * These define the toolbox packages available in the marketplace.
 * Each toolbox describes functions that are built-in to the engine
 * and provides documentation and example code for the marketplace.
 *
 * Users can "install" a toolbox to get:
 * - Helper functions defined as MATLAB scripts (loaded into environment)
 * - Documentation accessible via `man` command
 * - Example code and tutorials
 */
// ============================================================
// Signal Processing Toolbox
// ============================================================
export const signalProcessingToolbox = {
    id: "signal-processing-toolbox",
    title: "Signal Processing Toolbox",
    description: "Filter design, spectral analysis, signal generation, and digital signal processing functions compatible with MATLAB's Signal Processing Toolbox.",
    category: "signal-processing",
    tags: ["dsp", "filters", "fft", "spectral", "signals"],
    version: "1.0.0",
    code: `% Signal Processing Toolbox Helper Functions
% These extend the built-in signal processing functions

function y = bandpass(x, fpass, fs)
  % Bandpass filter using cascaded butterworth
  wn = fpass / (fs/2);
  result = butter(4, wn(1), 'high');
  b1 = result.b; a1 = result.a;
  result2 = butter(4, wn(2), 'low');  
  b2 = result2.b; a2 = result2.a;
  y = filter(b2, a2, filter(b1, a1, x));
end

function y = lowpass(x, fc, fs)
  % Lowpass filter wrapper
  wn = fc / (fs/2);
  result = butter(6, wn);
  y = filtfilt(result.b, result.a, x);
end

function y = highpass(x, fc, fs)
  % Highpass filter wrapper
  wn = fc / (fs/2);
  result = butter(6, wn, 'high');
  y = filtfilt(result.b, result.a, x);
end

function [f, P] = pwelch(x, window, noverlap, nfft, fs)
  % Welch power spectral density estimate
  if nargin < 5; fs = 1; end
  if nargin < 4; nfft = 256; end
  if nargin < 3; noverlap = nfft/2; end
  if nargin < 2; window = nfft; end
  P = periodogram(x);
  f = linspace(0, fs/2, length(P));
end

function snr_val = snr(x, noise)
  % Signal-to-noise ratio
  sig_power = mean(x .* x);
  noise_power = mean(noise .* noise);
  snr_val = 10 * log10(sig_power / noise_power);
end
`,
    builtinFunctions: [
        "fft",
        "ifft",
        "filter",
        "filtfilt",
        "conv",
        "butter",
        "fir1",
        "freqz",
        "periodogram",
        "spectrogram",
        "resample",
        "findpeaks",
        "sgolayfilt",
        "xcorr",
        "deconv",
        "hamming",
        "hanning",
        "blackman",
        "downsample",
        "upsample",
        "chirp",
        "sinc",
        "ema",
    ],
    functions: [
        {
            name: "butter",
            synopsis: "[b,a] = butter(n, Wn)\n[b,a] = butter(n, Wn, 'high')",
            description: "Design an Nth-order Butterworth digital filter. Returns the transfer function coefficients b (numerator) and a (denominator).",
            parameters: [
                {
                    name: "n",
                    type: "number",
                    description: "Filter order (positive integer)",
                },
                {
                    name: "Wn",
                    type: "number",
                    description: "Normalized cutoff frequency (0 to 1, where 1 = Nyquist)",
                },
                {
                    name: "type",
                    type: "string",
                    description: "'high' for highpass filter",
                    optional: true,
                    default: "'low'",
                },
            ],
            returns: "struct with fields b (numerator coefficients) and a (denominator coefficients)",
            examples: [
                {
                    code: "result = butter(4, 0.2)",
                    result: "struct with b and a vectors",
                    description: "4th-order lowpass Butterworth at 0.2×Nyquist",
                },
                {
                    code: "result = butter(2, 0.5, 'high')",
                    result: "highpass filter coefficients",
                    description: "2nd-order highpass at 0.5×Nyquist",
                },
            ],
            seeAlso: ["fir1", "filter", "filtfilt", "freqz"],
        },
        {
            name: "fir1",
            synopsis: "b = fir1(n, Wn)",
            description: "Design an Nth-order FIR lowpass filter using the windowed sinc method with a Hamming window.",
            parameters: [
                { name: "n", type: "number", description: "Filter order" },
                {
                    name: "Wn",
                    type: "number",
                    description: "Normalized cutoff frequency (0 to 1)",
                },
            ],
            returns: "FIR coefficient vector of length n+1",
            examples: [
                {
                    code: "b = fir1(20, 0.3)",
                    description: "20th-order lowpass FIR at 0.3×Nyquist",
                },
            ],
            seeAlso: ["butter", "filter", "freqz"],
        },
        {
            name: "filtfilt",
            synopsis: "y = filtfilt(b, a, x)",
            description: "Zero-phase digital filtering. Applies the filter forwards and then backwards to eliminate phase distortion.",
            parameters: [
                { name: "b", type: "array", description: "Numerator coefficients" },
                { name: "a", type: "array", description: "Denominator coefficients" },
                { name: "x", type: "array", description: "Input signal" },
            ],
            returns: "Filtered signal (same length as x)",
            examples: [
                {
                    code: "y = filtfilt([1 1]/2, 1, x)",
                    description: "Zero-phase 2-point moving average",
                },
            ],
            seeAlso: ["filter", "butter"],
        },
        {
            name: "freqz",
            synopsis: "[H, w] = freqz(b, a, N)",
            description: "Compute the frequency response of a digital filter defined by coefficients b and a.",
            parameters: [
                { name: "b", type: "array", description: "Numerator coefficients" },
                { name: "a", type: "array", description: "Denominator coefficients" },
                {
                    name: "N",
                    type: "number",
                    description: "Number of frequency points",
                    optional: true,
                    default: "512",
                },
            ],
            returns: "struct with fields w (frequency) and H (complex response)",
            examples: [{ code: "result = freqz([1 1], [1], 256)" }],
            seeAlso: ["butter", "fir1"],
        },
        {
            name: "findpeaks",
            synopsis: "[peaks, locs] = findpeaks(x)",
            description: "Find local maxima (peaks) in a signal. Returns peak values and their 1-based locations.",
            parameters: [{ name: "x", type: "array", description: "Input signal" }],
            returns: "struct with fields peaks (values) and locs (1-indexed positions)",
            examples: [
                {
                    code: "result = findpeaks([0 1 0 2 0 3 0])",
                    result: "peaks=[1,2,3], locs=[2,4,6]",
                },
            ],
            seeAlso: ["periodogram", "spectrogram"],
        },
        {
            name: "periodogram",
            synopsis: "Pxx = periodogram(x)",
            description: "Estimate the power spectral density of signal x using the periodogram method.",
            parameters: [{ name: "x", type: "array", description: "Input signal" }],
            returns: "Power spectral density estimate",
            examples: [{ code: "P = periodogram(randn(1, 128))" }],
            seeAlso: ["fft", "spectrogram"],
        },
        {
            name: "spectrogram",
            synopsis: "[S, f, t] = spectrogram(x, window, noverlap, nfft)",
            description: "Compute the short-time Fourier transform of signal x.",
            parameters: [
                { name: "x", type: "array", description: "Input signal" },
                {
                    name: "window",
                    type: "number",
                    description: "Window length",
                    optional: true,
                },
                {
                    name: "noverlap",
                    type: "number",
                    description: "Overlap samples",
                    optional: true,
                },
                {
                    name: "nfft",
                    type: "number",
                    description: "FFT length",
                    optional: true,
                },
            ],
            returns: "struct with fields S (spectrogram matrix), f (frequencies), t (times)",
            examples: [
                {
                    code: "result = spectrogram(chirp(linspace(0,1,1000), 10, 1, 100), 128, 64, 256)",
                },
            ],
            seeAlso: ["fft", "periodogram"],
        },
        {
            name: "sgolayfilt",
            synopsis: "y = sgolayfilt(x, order, framelen)",
            description: "Apply a Savitzky-Golay finite impulse response smoothing filter to the data in x.",
            parameters: [
                { name: "x", type: "array", description: "Input signal" },
                {
                    name: "order",
                    type: "number",
                    description: "Polynomial order (must be less than framelen)",
                },
                {
                    name: "framelen",
                    type: "number",
                    description: "Frame length (must be odd)",
                },
            ],
            returns: "Smoothed signal",
            examples: [{ code: "y = sgolayfilt(noisy_signal, 3, 11)" }],
            seeAlso: ["filtfilt", "movmean"],
        },
        {
            name: "resample",
            synopsis: "y = resample(x, p, q)",
            description: "Resample signal x at p/q times the original sampling rate.",
            parameters: [
                { name: "x", type: "array", description: "Input signal" },
                { name: "p", type: "number", description: "Upsample factor" },
                { name: "q", type: "number", description: "Downsample factor" },
            ],
            returns: "Resampled signal",
            examples: [
                { code: "y = resample(x, 3, 2)", description: "Upsample by 3/2" },
            ],
            seeAlso: ["upsample", "downsample"],
        },
        {
            name: "xcorr",
            synopsis: "r = xcorr(x)\nr = xcorr(x, y)",
            description: "Cross-correlation (or autocorrelation if y is omitted).",
            parameters: [
                { name: "x", type: "array", description: "First signal" },
                {
                    name: "y",
                    type: "array",
                    description: "Second signal",
                    optional: true,
                },
            ],
            returns: "Cross-correlation sequence",
            examples: [{ code: "r = xcorr([1 2 3], [1 0 1])" }],
            seeAlso: ["conv", "fft"],
        },
        {
            name: "chirp",
            synopsis: "y = chirp(t, f0, t1, f1)",
            description: "Generate a swept-frequency cosine (chirp) signal.",
            parameters: [
                { name: "t", type: "array", description: "Time vector" },
                { name: "f0", type: "number", description: "Initial frequency (Hz)" },
                { name: "t1", type: "number", description: "Target time" },
                { name: "f1", type: "number", description: "Frequency at t1 (Hz)" },
            ],
            returns: "Chirp signal values",
            examples: [{ code: "y = chirp(linspace(0,1,1000), 10, 1, 100)" }],
            seeAlso: ["sinc", "hamming"],
        },
    ],
    examples: [
        {
            title: "Butterworth Filter Design",
            description: "Design and apply a lowpass Butterworth filter",
            code: `% Design 4th order lowpass Butterworth filter
result = butter(4, 0.2);
b = result.b; a = result.a;

% Create noisy signal
t = linspace(0, 1, 1000);
x = sin(2*pi*5*t) + 0.5*sin(2*pi*50*t);

% Apply filter
y = filter(b, a, x);
plot(t, y)
title('Filtered Signal')`,
            output: "[Plot of filtered signal]",
        },
        {
            title: "Spectral Analysis",
            description: "Analyze frequency content of a signal",
            code: `t = linspace(0, 1, 1024);
x = sin(2*pi*50*t) + sin(2*pi*120*t);
P = periodogram(x);
plot(P)
title('Power Spectral Density')`,
        },
    ],
};
// ============================================================
// Communications Toolbox
// ============================================================
export const communicationsToolbox = {
    id: "communications-toolbox",
    title: "Communications Toolbox",
    description: "Digital modulation/demodulation (QAM, PSK), channel models (AWGN), error rate computation, and pulse shaping for communications system design.",
    category: "communications",
    tags: ["comms", "modulation", "qam", "psk", "awgn", "ber"],
    version: "1.0.0",
    code: `% Communications Toolbox Helper Functions

function ber = compute_ber(tx, rx)
  % Compute bit error rate between transmitted and received bits
  errors = biterr(tx, rx);
  ber = errors / length(tx);
end

function [tx_signal, rx_signal, ber] = qam_demo(M, snr_db, num_symbols)
  % Complete QAM modulation/demodulation demo
  if nargin < 3; num_symbols = 1000; end
  data = floor(rand(1, num_symbols) * M);
  tx_signal = qammod(data, M);
  rx_noisy = awgn(tx_signal, snr_db);
  rx_data = qamdemod(rx_noisy, M);
  ber = biterr(data, rx_data) / num_symbols;
  rx_signal = rx_noisy;
end

function ber_curve = ber_analysis(snr_range, mod_type, M)
  % Generate BER curve over SNR range
  ber_curve = zeros(1, length(snr_range));
  for i = 1:length(snr_range)
    ber_curve(i) = berawgn(snr_range(i), mod_type, M);
  end
end
`,
    builtinFunctions: [
        "awgn",
        "qammod",
        "qamdemod",
        "pskmod",
        "pskdemod",
        "biterr",
        "rcosdesign",
        "berawgn",
        "scatterplot",
    ],
    functions: [
        {
            name: "qammod",
            synopsis: "y = qammod(x, M)",
            description: "Quadrature amplitude modulation. Maps integer symbols (0 to M-1) to complex constellation points.",
            parameters: [
                {
                    name: "x",
                    type: "array",
                    description: "Integer symbol indices (0 to M-1)",
                },
                {
                    name: "M",
                    type: "number",
                    description: "Modulation order (must be a perfect square: 4, 16, 64, ...)",
                },
            ],
            returns: "Complex symbol array",
            examples: [
                { code: "y = qammod([0 1 2 3], 4)", description: "4-QAM modulation" },
                {
                    code: "scatterplot(qammod(0:15, 16))",
                    description: "16-QAM constellation",
                },
            ],
            seeAlso: ["qamdemod", "pskmod", "awgn"],
        },
        {
            name: "qamdemod",
            synopsis: "x = qamdemod(y, M)",
            description: "QAM demodulation. Maps complex symbols back to integer indices.",
            parameters: [
                { name: "y", type: "array", description: "Complex received symbols" },
                { name: "M", type: "number", description: "Modulation order" },
            ],
            returns: "Demodulated integer symbols",
            examples: [
                { code: "x = qamdemod(qammod([0 1 2 3], 4), 4)", result: "[0 1 2 3]" },
            ],
            seeAlso: ["qammod", "pskdemod"],
        },
        {
            name: "pskmod",
            synopsis: "y = pskmod(x, M)",
            description: "Phase shift keying modulation. Maps symbols to unit-circle constellation points.",
            parameters: [
                {
                    name: "x",
                    type: "array",
                    description: "Integer symbol indices (0 to M-1)",
                },
                {
                    name: "M",
                    type: "number",
                    description: "Modulation order (2=BPSK, 4=QPSK, 8=8PSK)",
                },
            ],
            returns: "Complex unit-amplitude symbol array",
            examples: [
                { code: "y = pskmod([0 1 2 3], 4)", description: "QPSK modulation" },
            ],
            seeAlso: ["pskdemod", "qammod"],
        },
        {
            name: "pskdemod",
            synopsis: "x = pskdemod(y, M)",
            description: "PSK demodulation.",
            parameters: [
                { name: "y", type: "array", description: "Complex received symbols" },
                { name: "M", type: "number", description: "Modulation order" },
            ],
            returns: "Demodulated integer symbols",
            examples: [
                { code: "x = pskdemod(pskmod([0 1 2 3], 4), 4)", result: "[0 1 2 3]" },
            ],
            seeAlso: ["pskmod", "qamdemod"],
        },
        {
            name: "awgn",
            synopsis: "y = awgn(x, snr)",
            description: "Add white Gaussian noise to signal x at the specified signal-to-noise ratio (in dB).",
            parameters: [
                { name: "x", type: "array", description: "Input signal" },
                {
                    name: "snr",
                    type: "number",
                    description: "Signal-to-noise ratio in dB",
                },
            ],
            returns: "Noisy signal",
            examples: [
                {
                    code: "y = awgn(ones(1,100), 20)",
                    description: "Add noise at 20 dB SNR",
                },
            ],
            seeAlso: ["qammod", "berawgn"],
        },
        {
            name: "biterr",
            synopsis: "errors = biterr(a, b)",
            description: "Count the number of bit errors between two integer arrays.",
            parameters: [
                { name: "a", type: "array", description: "First integer array" },
                { name: "b", type: "array", description: "Second integer array" },
            ],
            returns: "Number of bit errors",
            examples: [{ code: "e = biterr([0 1 1], [0 0 1])", result: "1" }],
            seeAlso: ["berawgn"],
        },
        {
            name: "berawgn",
            synopsis: "ber = berawgn(EbNo, modtype, M)",
            description: "Compute theoretical bit error rate (BER) for a given modulation type in an AWGN channel.",
            parameters: [
                { name: "EbNo", type: "number", description: "Eb/No in dB" },
                {
                    name: "modtype",
                    type: "string",
                    description: "Modulation type: 'psk' or 'qam'",
                },
                { name: "M", type: "number", description: "Modulation order" },
            ],
            returns: "Bit error rate (BER) scalar",
            examples: [
                {
                    code: "ber = berawgn(10, 'psk', 2)",
                    description: "BPSK BER at Eb/No = 10 dB",
                },
            ],
            seeAlso: ["biterr", "awgn"],
        },
        {
            name: "rcosdesign",
            synopsis: "h = rcosdesign(beta, span, sps)",
            description: "Design a raised cosine FIR filter for pulse shaping.",
            parameters: [
                {
                    name: "beta",
                    type: "number",
                    description: "Roll-off factor (0 to 1)",
                },
                { name: "span", type: "number", description: "Filter span in symbols" },
                { name: "sps", type: "number", description: "Samples per symbol" },
            ],
            returns: "FIR filter coefficient vector",
            examples: [
                {
                    code: "h = rcosdesign(0.25, 6, 4)",
                    description: "Root raised cosine filter",
                },
            ],
            seeAlso: ["filter", "conv"],
        },
    ],
    examples: [
        {
            title: "QAM Simulation",
            description: "16-QAM modulation and demodulation with AWGN channel",
            code: `% 16-QAM simulation
M = 16;
data = floor(rand(1, 1000) * M);
tx = qammod(data, M);

% Add noise
snr_db = 20;
rx = awgn(tx, snr_db);

% Demodulate
rx_data = qamdemod(rx, M);
errors = biterr(data, rx_data);
disp(sprintf('Bit errors: %d', errors))

% Show constellation
scatterplot(rx)`,
        },
        {
            title: "BER Analysis",
            description: "Theoretical BER curves for different modulation schemes",
            code: `% BER curves
snr = linspace(0, 15, 50);
ber_bpsk = zeros(1, 50);
ber_qpsk = zeros(1, 50);
for i = 1:50
  ber_bpsk(i) = berawgn(snr(i), 'psk', 2);
  ber_qpsk(i) = berawgn(snr(i), 'psk', 4);
end
plot(snr, ber_bpsk)
title('BER vs Eb/No')`,
        },
    ],
};
// ============================================================
// Optimization Toolbox
// ============================================================
export const optimizationToolbox = {
    id: "optimization-toolbox",
    title: "Optimization Toolbox",
    description: "Solvers for nonlinear optimization, root finding, linear/quadratic programming, and nonlinear least squares.",
    category: "optimization",
    tags: ["optimization", "minimization", "root-finding", "linear-programming"],
    version: "1.0.0",
    code: `% Optimization Toolbox Helper Functions

function [x, fval] = fminunc(fun, x0)
  % Unconstrained optimization (alias for fminsearch)
  x = fminsearch(fun, x0);
  fval = fun(x);
end

function x = bisect(fun, a, b, tol)
  % Bisection method root finder
  if nargin < 4; tol = 1e-10; end
  fa = fun(a);
  for i = 1:100
    c = (a + b) / 2;
    fc = fun(c);
    if abs(fc) < tol; x = c; return; end
    if fa * fc < 0
      b = c;
    else
      a = c; fa = fc;
    end
  end
  x = (a + b) / 2;
end
`,
    builtinFunctions: [
        "fminbnd",
        "fminsearch",
        "fzero",
        "linprog",
        "quadprog",
        "lsqnonlin",
    ],
    functions: [
        {
            name: "fminbnd",
            synopsis: "x = fminbnd(fun, a, b)",
            description: "Find the minimum of a single-variable function on a bounded interval [a, b] using golden section search.",
            parameters: [
                {
                    name: "fun",
                    type: "function_handle",
                    description: "Objective function @(x) f(x)",
                },
                { name: "a", type: "number", description: "Lower bound" },
                { name: "b", type: "number", description: "Upper bound" },
            ],
            returns: "Value of x that minimizes fun on [a, b]",
            examples: [
                {
                    code: "x = fminbnd(@(x) (x-2)^2, 0, 4)",
                    result: "≈ 2",
                    description: "Minimum of (x-2)² on [0,4]",
                },
            ],
            seeAlso: ["fminsearch", "fzero"],
        },
        {
            name: "fminsearch",
            synopsis: "x = fminsearch(fun, x0)",
            description: "Find the unconstrained minimum of a multivariable function using the Nelder-Mead simplex algorithm.",
            parameters: [
                {
                    name: "fun",
                    type: "function_handle",
                    description: "Objective function",
                },
                { name: "x0", type: "number | array", description: "Initial guess" },
            ],
            returns: "Value of x that minimizes fun",
            examples: [{ code: "x = fminsearch(@(x) (x-3)^2, 0)", result: "≈ 3" }],
            seeAlso: ["fminbnd", "lsqnonlin"],
        },
        {
            name: "fzero",
            synopsis: "x = fzero(fun, x0)",
            description: "Find a zero of a function using Newton's method with numerical derivatives.",
            parameters: [
                {
                    name: "fun",
                    type: "function_handle",
                    description: "Function to find zero of",
                },
                { name: "x0", type: "number", description: "Initial guess" },
            ],
            returns: "Value of x where fun(x) ≈ 0",
            examples: [
                { code: "x = fzero(@(x) x^2 - 4, 1)", result: "≈ 2" },
                { code: "x = fzero(@(x) cos(x), 1)", result: "≈ 1.5708 (π/2)" },
            ],
            seeAlso: ["fminbnd"],
        },
        {
            name: "linprog",
            synopsis: "x = linprog(f, A, b)",
            description: "Solve a linear programming problem: minimize f'*x subject to A*x ≤ b, x ≥ 0.",
            parameters: [
                {
                    name: "f",
                    type: "array",
                    description: "Objective function coefficients",
                },
                {
                    name: "A",
                    type: "matrix",
                    description: "Inequality constraint matrix",
                },
                {
                    name: "b",
                    type: "array",
                    description: "Inequality constraint vector",
                },
            ],
            returns: "Optimal solution vector",
            examples: [{ code: "x = linprog([-1; -2], [1 1; 2 1], [4; 6])" }],
            seeAlso: ["quadprog"],
        },
        {
            name: "lsqnonlin",
            synopsis: "x = lsqnonlin(fun, x0)",
            description: "Solve nonlinear least-squares problems: minimize ||fun(x)||² using Levenberg-Marquardt.",
            parameters: [
                {
                    name: "fun",
                    type: "function_handle",
                    description: "Residual function returning vector",
                },
                {
                    name: "x0",
                    type: "number | array",
                    description: "Initial parameter guess",
                },
            ],
            returns: "Optimal parameter values",
            examples: [
                { code: "x = lsqnonlin(@(x) [x-1, 2*(x-2)], 0)", result: "≈ 1.4" },
            ],
            seeAlso: ["fminsearch"],
        },
    ],
    examples: [
        {
            title: "Root Finding",
            description: "Find roots of nonlinear equations",
            code: `% Find where x^3 - 2x - 5 = 0
x = fzero(@(x) x^3 - 2*x - 5, 2)
disp(sprintf('Root: %.6f', x))

% Verify
disp(sprintf('f(x) = %.2e', x^3 - 2*x - 5))`,
        },
        {
            title: "Curve Fitting",
            description: "Fit a model to data using nonlinear least squares",
            code: `% Fit y = a*exp(b*x) to data
xdata = [0 1 2 3 4 5];
ydata = [1 2.7 7.4 20 55 148];
params = lsqnonlin(@(p) p(1)*exp(p(2)*xdata) - ydata, [1 0.5])
disp(sprintf('a = %.2f, b = %.2f', params(1), params(2)))`,
        },
    ],
};
// ============================================================
// Statistics and Machine Learning Toolbox
// ============================================================
export const statisticsToolbox = {
    id: "statistics-ml-toolbox",
    title: "Statistics and Machine Learning Toolbox",
    description: "Statistical analysis, hypothesis testing, regression, clustering, and PCA.",
    category: "statistics",
    tags: ["statistics", "regression", "clustering", "pca", "machine-learning"],
    version: "1.0.0",
    code: `% Statistics and Machine Learning Helper Functions

function r = zscore(x)
  % Standardize data to zero mean and unit variance
  m = mean(x);
  s = std(x);
  r = (x - m) / s;
end

function r = normalize(x)
  % Normalize to [0, 1] range
  mn = min(x);
  mx = max(x);
  r = (x - mn) / (mx - mn);
end

function [b, stats] = regress(y, X)
  % Multiple linear regression
  % Returns coefficients and R-squared
  n = length(y);
  p = size(X, 2);
  b = polyfit(X(:,1), y, 1);
  yhat = polyval(b, X(:,1));
  ss_res = sum((y - yhat).^2);
  ss_tot = sum((y - mean(y)).^2);
  stats.rsquared = 1 - ss_res / ss_tot;
  stats.coefficients = b;
end

function d = pdist(X)
  % Pairwise Euclidean distances
  n = size(X, 1);
  d = zeros(n, n);
  for i = 1:n
    for j = i+1:n
      diff = X(i,:) - X(j,:);
      d(i,j) = sqrt(sum(diff.^2));
      d(j,i) = d(i,j);
    end
  end
end
`,
    builtinFunctions: [
        "mean",
        "std",
        "var",
        "median",
        "corrcoef",
        "cov",
        "prctile",
        "histcounts",
        "movmean",
        "fitlm",
        "kmeans",
        "pca",
        "ttest",
    ],
    functions: [
        {
            name: "fitlm",
            synopsis: "mdl = fitlm(X, y)",
            description: "Fit a simple linear regression model y = b0 + b1*x. Returns model with coefficients and R-squared.",
            parameters: [
                { name: "X", type: "array", description: "Predictor data" },
                { name: "y", type: "array", description: "Response data" },
            ],
            returns: "struct with fields: intercept, slope, rSquared, coefficients",
            examples: [
                {
                    code: "mdl = fitlm([1 2 3 4], [2 4 6 8])",
                    result: "slope ≈ 2, intercept ≈ 0",
                    description: "Linear fit to perfectly linear data",
                },
            ],
            seeAlso: ["polyfit", "corrcoef"],
        },
        {
            name: "kmeans",
            synopsis: "[idx, C] = kmeans(X, k)",
            description: "Partition data into k clusters using K-means algorithm.",
            parameters: [
                {
                    name: "X",
                    type: "matrix",
                    description: "Data matrix (rows = observations)",
                },
                { name: "k", type: "number", description: "Number of clusters" },
            ],
            returns: "struct with fields: labels (cluster assignments, 1-indexed), centroids",
            examples: [
                {
                    code: "result = kmeans([1;2;10;11], 2)",
                    result: "labels=[1,1,2,2]",
                    description: "Cluster into 2 groups",
                },
            ],
            seeAlso: ["pca"],
        },
        {
            name: "pca",
            synopsis: "[coeff, score, latent] = pca(X)",
            description: "Principal component analysis. Reduces dimensionality by finding directions of maximum variance.",
            parameters: [
                {
                    name: "X",
                    type: "matrix",
                    description: "Data matrix (rows = observations, columns = variables)",
                },
            ],
            returns: "struct with fields: coeff (loadings), score (transformed data), latent (eigenvalues)",
            examples: [{ code: "result = pca(randn(100, 3))" }],
            seeAlso: ["kmeans", "corrcoef"],
        },
        {
            name: "ttest",
            synopsis: "[h, p] = ttest(x)",
            description: "One-sample t-test. Tests the null hypothesis that data comes from a distribution with mean 0.",
            parameters: [{ name: "x", type: "array", description: "Sample data" }],
            returns: "struct with fields: h (0 or 1 reject), p (p-value), tstat, mean",
            examples: [
                {
                    code: "result = ttest([1 2 3 4 5])",
                    description: "Test if mean significantly differs from 0",
                },
            ],
            seeAlso: ["mean", "std"],
        },
    ],
    examples: [
        {
            title: "Regression Analysis",
            description: "Fit a linear model and assess quality",
            code: `x = [1 2 3 4 5 6 7 8 9 10];
y = 2*x + 1 + randn(1,10)*0.5;
mdl = fitlm(x, y)
disp(sprintf('Slope: %.2f', mdl.slope))
disp(sprintf('R²: %.4f', mdl.rSquared))`,
        },
        {
            title: "K-means Clustering",
            description: "Cluster 2D data into groups",
            code: `% Create clustered data
X = [randn(20,1)+2, randn(20,1)+2; randn(20,1)-2, randn(20,1)-2];
result = kmeans(X, 2)
disp(result.centroids)`,
        },
    ],
};
// ============================================================
// Control Systems Toolbox
// ============================================================
export const controlSystemsToolbox = {
    id: "control-systems-toolbox",
    title: "Control System Toolbox",
    description: "Transfer function models, state-space representations, frequency response analysis, stability margins, and system interconnection.",
    category: "control-systems",
    tags: ["control", "transfer-function", "bode", "stability", "feedback"],
    version: "1.0.0",
    code: `% Control System Toolbox Helper Functions

function [y, t] = lsim(sys, u, t)
  % Linear simulation with arbitrary input
  b = sys.num; a = sys.den;
  y = filter(b, a, u);
end

function sys = series(sys1, sys2)
  % Series interconnection: sys1 * sys2
  num = conv(sys1.num, sys2.num);
  den = conv(sys1.den, sys2.den);
  sys = tf(num, den);
end

function sys = parallel(sys1, sys2)
  % Parallel interconnection: sys1 + sys2
  num = conv(sys1.num, sys2.den) + conv(sys2.num, sys1.den);
  den = conv(sys1.den, sys2.den);
  sys = tf(num, den);
end
`,
    builtinFunctions: [
        "tf",
        "ss",
        "zpk",
        "step",
        "impulse",
        "bode",
        "margin",
        "feedback",
        "c2d",
    ],
    functions: [
        {
            name: "tf",
            synopsis: "sys = tf(num, den)",
            description: "Create a transfer function model from numerator and denominator polynomial coefficients.",
            parameters: [
                {
                    name: "num",
                    type: "array",
                    description: "Numerator polynomial coefficients [bn ... b1 b0]",
                },
                {
                    name: "den",
                    type: "array",
                    description: "Denominator polynomial coefficients [an ... a1 a0]",
                },
            ],
            returns: "Transfer function model struct",
            examples: [
                { code: "sys = tf([1], [1 2 1])", description: "1/(s²+2s+1)" },
                { code: "sys = tf([1 0], [1 1])", description: "s/(s+1)" },
            ],
            seeAlso: ["ss", "zpk", "step", "bode"],
        },
        {
            name: "step",
            synopsis: "[y, t] = step(sys)\n[y, t] = step(sys, tFinal)",
            description: "Compute the step response of a linear system.",
            parameters: [
                { name: "sys", type: "tf", description: "Transfer function model" },
                {
                    name: "tFinal",
                    type: "number",
                    description: "Final simulation time",
                    optional: true,
                    default: "10",
                },
            ],
            returns: "struct with fields: t (time vector) and y (response vector)",
            examples: [
                {
                    code: "result = step(tf([1],[1 1]))",
                    description: "Step response of 1/(s+1)",
                },
            ],
            seeAlso: ["impulse", "bode", "tf"],
        },
        {
            name: "impulse",
            synopsis: "[y, t] = impulse(sys)",
            description: "Compute the impulse response of a linear system.",
            parameters: [
                { name: "sys", type: "tf", description: "Transfer function model" },
                {
                    name: "tFinal",
                    type: "number",
                    description: "Final simulation time",
                    optional: true,
                },
            ],
            returns: "struct with fields: t and y",
            examples: [{ code: "result = impulse(tf([1],[1 1]))" }],
            seeAlso: ["step", "bode"],
        },
        {
            name: "bode",
            synopsis: "[mag, phase, w] = bode(sys)",
            description: "Compute the Bode magnitude and phase of a frequency response.",
            parameters: [
                { name: "sys", type: "tf", description: "Transfer function model" },
            ],
            returns: "struct with fields: w (frequency), mag (magnitude in dB), phase (phase in degrees)",
            examples: [{ code: "result = bode(tf([1],[1 1 1]))" }],
            seeAlso: ["margin", "step", "tf"],
        },
        {
            name: "margin",
            synopsis: "[Gm, Pm, Wcg, Wcp] = margin(sys)",
            description: "Compute gain margin, phase margin, and crossover frequencies.",
            parameters: [
                { name: "sys", type: "tf", description: "Transfer function model" },
            ],
            returns: "struct with fields: gainMargin, phaseMargin, gmFreq, pmFreq",
            examples: [{ code: "result = margin(tf([1],[1 1 1]))" }],
            seeAlso: ["bode", "feedback"],
        },
        {
            name: "feedback",
            synopsis: "sys_cl = feedback(G, H)",
            description: "Compute the closed-loop transfer function G/(1+G*H) for negative feedback.",
            parameters: [
                {
                    name: "G",
                    type: "tf",
                    description: "Forward path transfer function",
                },
                {
                    name: "H",
                    type: "number | tf",
                    description: "Feedback path (use 1 for unity feedback)",
                },
            ],
            returns: "Closed-loop transfer function",
            examples: [
                {
                    code: "sys_cl = feedback(tf([10],[1 1]), 1)",
                    description: "Unity feedback for G=10/(s+1)",
                },
            ],
            seeAlso: ["tf", "margin"],
        },
        {
            name: "c2d",
            synopsis: "sys_d = c2d(sys, Ts)",
            description: "Convert continuous-time system to discrete-time using zero-order hold.",
            parameters: [
                { name: "sys", type: "tf", description: "Continuous-time system" },
                { name: "Ts", type: "number", description: "Sampling period" },
            ],
            returns: "Discrete-time transfer function with Ts field",
            examples: [{ code: "sys_d = c2d(tf([1],[1 1]), 0.1)" }],
            seeAlso: ["tf"],
        },
    ],
    examples: [
        {
            title: "PID Controller Design",
            description: "Design a PID controller for a second-order plant",
            code: `% Plant: 1/(s^2 + 2s + 1)
G = tf([1], [1 2 1]);

% Unity feedback step response
sys_cl = feedback(G, 1);
result = step(sys_cl);
plot(result.t, result.y)
title('Closed-Loop Step Response')`,
        },
        {
            title: "Frequency Response",
            description: "Bode plot and stability margins",
            code: `% Open-loop transfer function
G = tf([10], [1 3 2]);
result = bode(G);
m = margin(G);
disp(sprintf('Gain margin: %.1f dB', m.gainMargin))
disp(sprintf('Phase margin: %.1f deg', m.phaseMargin))`,
        },
    ],
};
// ============================================================
// Linear Algebra Package
// ============================================================
export const linearAlgebraToolbox = {
    id: "linear-algebra-toolbox",
    title: "Linear Algebra Package",
    description: "Matrix decompositions (SVD, QR, LU, eigendecomposition), pseudoinverse, condition numbers, and matrix analysis functions.",
    category: "mathematics",
    tags: ["linear-algebra", "matrices", "svd", "eigenvalues", "decomposition"],
    version: "1.0.0",
    code: `% Linear Algebra Helper Functions

function x = linsolve(A, b)
  % Solve Ax = b
  x = inv(A) * b;
end

function [V, D] = eigs(A, k)
  % Find k largest eigenvalues
  eigenvalues = eig(A);
  V = eigenvalues(1:min(k, length(eigenvalues)));
  D = diag(V);
end

function H = hilbert(n)
  % Generate Hilbert matrix
  H = zeros(n, n);
  for i = 1:n
    for j = 1:n
      H(i, j) = 1 / (i + j - 1);
    end
  end
end

function V = vander(x)
  % Vandermonde matrix
  n = length(x);
  V = zeros(n, n);
  for i = 1:n
    for j = 1:n
      V(i, j) = x(i)^(n - j);
    end
  end
end
`,
    builtinFunctions: [
        "det",
        "trace",
        "inv",
        "eig",
        "svd",
        "qr",
        "lu",
        "rank",
        "pinv",
        "norm",
        "cond",
        "tril",
        "triu",
        "kron",
        "eye",
        "zeros",
        "ones",
        "diag",
    ],
    functions: [
        {
            name: "svd",
            synopsis: "s = svd(A)",
            description: "Compute singular values of matrix A. Returns singular values in descending order.",
            parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
            returns: "Array of singular values (descending)",
            examples: [
                { code: "s = svd([3 0; 0 4])", result: "[4, 3]" },
                { code: "s = svd([1 2; 3 4])", result: "≈ [5.46, 0.37]" },
            ],
            seeAlso: ["eig", "rank", "pinv"],
        },
        {
            name: "qr",
            synopsis: "[Q, R] = qr(A)",
            description: "QR factorization using Gram-Schmidt orthogonalization. A = Q*R where Q is orthogonal and R is upper triangular.",
            parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
            returns: "struct with fields Q (orthogonal) and R (upper triangular)",
            examples: [{ code: "result = qr([1 2; 3 4])" }],
            seeAlso: ["lu", "svd"],
        },
        {
            name: "rank",
            synopsis: "r = rank(A)",
            description: "Compute the numerical rank of a matrix using Gaussian elimination with pivoting.",
            parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
            returns: "Integer rank",
            examples: [
                { code: "rank([1 2; 2 4])", result: "1" },
                { code: "rank(eye(3))", result: "3" },
            ],
            seeAlso: ["svd", "det"],
        },
        {
            name: "pinv",
            synopsis: "B = pinv(A)",
            description: "Moore-Penrose pseudoinverse. pinv(A) = (A'A)^(-1) * A' for full column rank.",
            parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
            returns: "Pseudoinverse matrix",
            examples: [{ code: "pinv([1 0; 0 0])", result: "[[1 0]; [0 0]]" }],
            seeAlso: ["inv", "rank"],
        },
        {
            name: "cond",
            synopsis: "c = cond(A)",
            description: "Condition number of a matrix (ratio of largest to smallest singular value).",
            parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
            returns: "Condition number (≥ 1, Inf for singular)",
            examples: [{ code: "cond(eye(3))", result: "1" }],
            seeAlso: ["svd", "rank"],
        },
    ],
    examples: [
        {
            title: "Matrix Decomposition",
            description: "SVD and QR decomposition examples",
            code: `A = [1 2 3; 4 5 6; 7 8 10];
disp('Singular values:')
disp(svd(A))
result = qr(A);
disp('QR decomposition R:')
disp(result.R)
disp(sprintf('Rank: %d', rank(A)))
disp(sprintf('Condition: %.2f', cond(A)))`,
        },
    ],
};
// ============================================================
// Image Processing Toolbox
// ============================================================
const imageProcessingToolbox = {
    id: "image-processing-toolbox",
    title: "Image Processing Toolbox",
    description: "Image filtering, enhancement, segmentation, edge detection, morphological operations, and spatial transformations compatible with MATLAB's Image Processing Toolbox.",
    category: "image-processing",
    tags: ["image", "filtering", "edge-detection", "segmentation", "morphology"],
    version: "1.0.0",
    code: `% Image Processing Toolbox Helper Functions

function I = im2double(img)
  % Convert image to double precision in [0,1]
  I = img / 255;
end

function I = im2gray(img)
  % Alias for rgb2gray
  I = rgb2gray(img);
end

function BW = imbinarize(I, threshold)
  % Binarize grayscale image using threshold
  if nargin < 2; threshold = 0.5; end
  BW = I > threshold;
end
`,
    builtinFunctions: [
        "imread",
        "imwrite",
        "imshow",
        "rgb2gray",
        "imresize",
        "imfilter",
        "edge",
        "bwlabel",
        "regionprops",
        "fspecial",
    ],
    functions: [
        {
            name: "imread",
            synopsis: "I = imread(filename)",
            description: "Read image from file. Returns a numeric matrix representing the image pixel values.",
            parameters: [
                {
                    name: "filename",
                    type: "string",
                    description: "Path to image file (PNG, JPEG, BMP, etc.)",
                },
            ],
            returns: "Numeric matrix of pixel values (H×W for grayscale, H×W×3 for RGB)",
            examples: [
                { code: "I = imread('photo.png')", result: "Image matrix", description: "Read a PNG image" },
            ],
            seeAlso: ["imwrite", "imshow"],
        },
        {
            name: "imshow",
            synopsis: "imshow(I)",
            description: "Display image in a figure window. Automatically scales display range based on data type.",
            parameters: [
                { name: "I", type: "matrix", description: "Image matrix to display" },
            ],
            returns: "Figure display",
            examples: [
                { code: "imshow(eye(8))", result: "Displays 8×8 identity as image" },
            ],
            seeAlso: ["imread", "imwrite"],
        },
        {
            name: "rgb2gray",
            synopsis: "I = rgb2gray(RGB)",
            description: "Convert RGB color image to grayscale using standard luminance weights (0.2989*R + 0.5870*G + 0.1140*B).",
            parameters: [
                { name: "RGB", type: "matrix", description: "H×W×3 RGB image matrix" },
            ],
            returns: "H×W grayscale image matrix",
            examples: [
                { code: "gray = rgb2gray(cat(3, ones(2), zeros(2), zeros(2)))", result: "0.2989 matrix" },
            ],
            seeAlso: ["imread", "imshow"],
        },
        {
            name: "imresize",
            synopsis: "J = imresize(I, scale)\nJ = imresize(I, [rows cols])",
            description: "Resize image by scale factor or to specified output dimensions using bilinear interpolation.",
            parameters: [
                { name: "I", type: "matrix", description: "Input image" },
                { name: "scale", type: "number or array", description: "Scale factor or [rows cols] target size" },
            ],
            returns: "Resized image matrix",
            examples: [
                { code: "J = imresize(eye(4), 0.5)", result: "2×2 resized image" },
            ],
            seeAlso: ["imread", "imshow"],
        },
        {
            name: "imfilter",
            synopsis: "J = imfilter(I, h)",
            description: "Apply 2-D spatial filter h to image I using correlation. Common filters can be created with fspecial.",
            parameters: [
                { name: "I", type: "matrix", description: "Input image" },
                { name: "h", type: "matrix", description: "Filter kernel (e.g., from fspecial)" },
            ],
            returns: "Filtered image matrix",
            examples: [
                { code: "J = imfilter(I, fspecial('average', 3))", result: "3×3 average-filtered image" },
            ],
            seeAlso: ["fspecial", "edge", "conv2"],
        },
        {
            name: "edge",
            synopsis: "BW = edge(I, method)",
            description: "Find edges in grayscale image using the specified method. Supports 'sobel', 'prewitt', and 'canny' methods.",
            parameters: [
                { name: "I", type: "matrix", description: "Grayscale input image" },
                { name: "method", type: "string", description: "'sobel' (default), 'prewitt', or 'canny'", optional: true, default: "'sobel'" },
            ],
            returns: "Binary edge map (logical matrix)",
            examples: [
                { code: "BW = edge(I, 'sobel')", result: "Binary edge image" },
            ],
            seeAlso: ["imfilter", "fspecial"],
        },
        {
            name: "bwlabel",
            synopsis: "L = bwlabel(BW)",
            description: "Label connected components in binary image. Each connected region gets a unique integer label.",
            parameters: [
                { name: "BW", type: "matrix", description: "Binary image (logical)" },
            ],
            returns: "Label matrix with integer labels for each connected component",
            examples: [
                { code: "L = bwlabel([1 0 1; 0 0 0; 1 0 1])", result: "Label matrix with 4 components" },
            ],
            seeAlso: ["regionprops", "edge"],
        },
        {
            name: "regionprops",
            synopsis: "stats = regionprops(BW, property)",
            description: "Measure properties of image regions. Returns struct array with requested measurements.",
            parameters: [
                { name: "BW", type: "matrix", description: "Binary or label image" },
                { name: "property", type: "string", description: "'Area', 'Centroid', 'BoundingBox', etc.", optional: true, default: "'Area'" },
            ],
            returns: "Struct array with region measurements",
            examples: [
                { code: "stats = regionprops([1 1; 0 0], 'Area')", result: "struct with Area = 2" },
            ],
            seeAlso: ["bwlabel", "edge"],
        },
        {
            name: "fspecial",
            synopsis: "h = fspecial(type, size)",
            description: "Create predefined 2-D filter kernels. Supports 'average', 'gaussian', 'sobel', 'prewitt', 'laplacian'.",
            parameters: [
                { name: "type", type: "string", description: "'average', 'gaussian', 'sobel', 'prewitt', 'laplacian'" },
                { name: "size", type: "number", description: "Filter size (default 3)", optional: true, default: "3" },
            ],
            returns: "Filter kernel matrix",
            examples: [
                { code: "h = fspecial('average', 3)", result: "3×3 averaging filter (all 1/9)" },
                { code: "h = fspecial('sobel')", result: "3×3 Sobel edge kernel" },
            ],
            seeAlso: ["imfilter", "edge", "conv2"],
        },
    ],
    examples: [
        {
            title: "Edge Detection Pipeline",
            description: "Detect edges in an image using the Sobel operator",
            code: `% Create a simple test pattern
I = zeros(8, 8);
I(3:6, 3:6) = 1;
BW = edge(I, 'sobel');
imshow(BW)
title('Edge Detection')`,
        },
        {
            title: "Image Filtering",
            description: "Apply average and Gaussian filters",
            code: `% Create noisy image
I = eye(8) + rand(8, 8) * 0.3;
h = fspecial('average', 3);
J = imfilter(I, h);
imshow(J)
title('Filtered Image')`,
        },
    ],
};
// ============================================================
// Symbolic Math Toolbox
// ============================================================
const symbolicMathToolbox = {
    id: "symbolic-math-toolbox",
    title: "Symbolic Math Toolbox",
    description: "Symbolic computation including differentiation, integration, equation solving, simplification, and substitution compatible with MATLAB's Symbolic Math Toolbox.",
    category: "symbolic-math",
    tags: ["symbolic", "calculus", "algebra", "differentiation", "integration"],
    version: "1.0.0",
    code: `% Symbolic Math Toolbox Helper Functions

function result = taylor(f, x, n)
  % Taylor series expansion (symbolic approximation)
  % Returns string representation of Taylor polynomial
  result = sym(f);
end

function result = limit(f, x, a)
  % Compute limit of f as x approaches a
  result = subs(f, x, a);
end
`,
    builtinFunctions: [
        "syms",
        "sym",
        "diff",
        "int",
        "solve",
        "subs",
        "simplify",
    ],
    functions: [
        {
            name: "syms",
            synopsis: "syms x\nsyms x y z",
            description: "Declare one or more symbolic variables. Creates symbolic variable representations in the workspace.",
            parameters: [
                {
                    name: "vars",
                    type: "identifiers",
                    description: "Variable names to declare as symbolic",
                },
            ],
            returns: "Symbolic variable(s) bound in workspace",
            examples: [
                { code: "syms x", result: "Symbolic variable x", description: "Declare single variable" },
                { code: "syms x y z", result: "Three symbolic variables", description: "Declare multiple variables" },
            ],
            seeAlso: ["sym", "solve", "diff"],
        },
        {
            name: "sym",
            synopsis: "s = sym(expr)",
            description: "Create a symbolic expression from a string representation. Parses mathematical notation into symbolic form.",
            parameters: [
                {
                    name: "expr",
                    type: "string",
                    description: "Expression string like 'x^2 + 1'",
                },
            ],
            returns: "Symbolic expression representation",
            examples: [
                { code: "f = sym('x^2 + 2*x + 1')", result: "Symbolic: x^2 + 2*x + 1" },
            ],
            seeAlso: ["syms", "subs", "simplify"],
        },
        {
            name: "diff",
            synopsis: "df = diff(f, x)\ndf = diff(f, x, n)",
            description: "Compute the symbolic derivative of expression f with respect to variable x. Supports nth-order derivatives.",
            parameters: [
                { name: "f", type: "symbolic", description: "Symbolic expression to differentiate" },
                { name: "x", type: "symbolic", description: "Variable to differentiate with respect to" },
                { name: "n", type: "number", description: "Order of derivative (default 1)", optional: true },
            ],
            returns: "Symbolic derivative expression",
            examples: [
                { code: "syms x; diff(x^3, x)", result: "3*x^2", description: "First derivative" },
                { code: "syms x; diff(sin(x), x)", result: "cos(x)", description: "Derivative of sin" },
            ],
            seeAlso: ["int", "subs", "simplify"],
        },
        {
            name: "int",
            synopsis: "F = int(f, x)\nF = int(f, x, a, b)",
            description: "Compute symbolic integral of f with respect to x. Supports both indefinite and definite integration.",
            parameters: [
                { name: "f", type: "symbolic", description: "Expression to integrate" },
                { name: "x", type: "symbolic", description: "Integration variable" },
                { name: "a", type: "number", description: "Lower limit (for definite integral)", optional: true },
                { name: "b", type: "number", description: "Upper limit (for definite integral)", optional: true },
            ],
            returns: "Symbolic integral expression or numeric value",
            examples: [
                { code: "syms x; int(x^2, x)", result: "x^3/3", description: "Indefinite integral" },
                { code: "syms x; int(x, x, 0, 1)", result: "0.5", description: "Definite integral" },
            ],
            seeAlso: ["diff", "subs"],
        },
        {
            name: "solve",
            synopsis: "x = solve(eq, x)",
            description: "Solve symbolic equation for specified variable. Returns solutions as symbolic expression.",
            parameters: [
                { name: "eq", type: "symbolic", description: "Equation to solve (e.g., x^2 - 1 == 0)" },
                { name: "x", type: "symbolic", description: "Variable to solve for" },
            ],
            returns: "Solutions array",
            examples: [
                { code: "syms x; solve(x^2 - 4, x)", result: "[-2, 2]", description: "Solve quadratic" },
            ],
            seeAlso: ["syms", "subs"],
        },
        {
            name: "subs",
            synopsis: "result = subs(f, x, value)",
            description: "Substitute a value for a symbolic variable in an expression. Evaluates the expression at the given point.",
            parameters: [
                { name: "f", type: "symbolic", description: "Symbolic expression" },
                { name: "x", type: "symbolic", description: "Variable to replace" },
                { name: "value", type: "number", description: "Value to substitute" },
            ],
            returns: "Evaluated result",
            examples: [
                { code: "syms x; subs(x^2, x, 3)", result: "9" },
            ],
            seeAlso: ["sym", "solve"],
        },
        {
            name: "simplify",
            synopsis: "result = simplify(expr)",
            description: "Algebraically simplify a symbolic expression. Applies standard simplification rules.",
            parameters: [
                { name: "expr", type: "symbolic", description: "Expression to simplify" },
            ],
            returns: "Simplified symbolic expression",
            examples: [
                { code: "simplify('sin(x)^2 + cos(x)^2')", result: "1", description: "Pythagorean identity" },
                { code: "simplify('(x^2 - 1)/(x - 1)')", result: "x + 1", description: "Factor cancellation" },
            ],
            seeAlso: ["sym", "subs"],
        },
    ],
    examples: [
        {
            title: "Calculus Operations",
            description: "Differentiation and integration of polynomial expressions",
            code: `syms x
f = sym('x^3 + 2*x^2 - x + 5')
df = diff(f, x)
F = int(f, x)
disp(df)
disp(F)`,
        },
        {
            title: "Solving Equations",
            description: "Solve polynomial equations symbolically",
            code: `syms x
roots = solve(x^2 - 5*x + 6, x)
disp(roots)`,
        },
    ],
};
// ============================================================
// Deep Learning Toolbox
// ============================================================
const deepLearningToolbox = {
    id: "deep-learning-toolbox",
    title: "Deep Learning Toolbox",
    description: "Neural network layers, training workflows, and inference functions for building deep learning models compatible with MATLAB's Deep Learning Toolbox.",
    category: "deep-learning",
    tags: ["neural-network", "deep-learning", "cnn", "classification", "training"],
    version: "1.0.0",
    code: `% Deep Learning Toolbox Helper Functions

function acc = accuracy(predictions, targets)
  % Compute classification accuracy
  correct = sum(predictions == targets);
  acc = correct / length(targets);
end

function [trainX, testX, trainY, testY] = train_test_split(X, Y, ratio)
  % Split data into training and test sets
  if nargin < 3; ratio = 0.8; end
  n = size(X, 1);
  idx = floor(n * ratio);
  trainX = X(1:idx, :);
  testX = X(idx+1:end, :);
  trainY = Y(1:idx);
  testY = Y(idx+1:end);
end
`,
    builtinFunctions: [
        "imageInputLayer",
        "fullyConnectedLayer",
        "convolution2dLayer",
        "maxPooling2dLayer",
        "reluLayer",
        "batchNormalizationLayer",
        "dropoutLayer",
        "softmaxLayer",
        "classificationLayer",
        "dlnetwork",
        "trainingOptions",
        "trainnet",
        "predict",
        "classify",
        "relu",
        "sigmoid",
        "softmax",
        "crossentropy",
    ],
    functions: [
        {
            name: "imageInputLayer",
            synopsis: "layer = imageInputLayer([h w c])",
            description: "Create an image input layer specifying the input image size as [height width channels].",
            parameters: [
                { name: "inputSize", type: "array", description: "[height width channels] of input images" },
            ],
            returns: "Image input layer object",
            examples: [
                { code: "layer = imageInputLayer([28 28 1])", result: "28×28×1 input layer", description: "MNIST-sized input" },
            ],
            seeAlso: ["fullyConnectedLayer", "dlnetwork"],
        },
        {
            name: "fullyConnectedLayer",
            synopsis: "layer = fullyConnectedLayer(outputSize)",
            description: "Create a fully connected (dense) layer with the specified number of output neurons.",
            parameters: [
                { name: "outputSize", type: "number", description: "Number of output neurons" },
            ],
            returns: "Fully connected layer object",
            examples: [
                { code: "layer = fullyConnectedLayer(10)", result: "Dense layer with 10 outputs" },
            ],
            seeAlso: ["imageInputLayer", "reluLayer", "softmaxLayer"],
        },
        {
            name: "convolution2dLayer",
            synopsis: "layer = convolution2dLayer(filterSize, numFilters)",
            description: "Create a 2-D convolutional layer with specified filter size and number of filters.",
            parameters: [
                { name: "filterSize", type: "number or array", description: "Size of convolution filters" },
                { name: "numFilters", type: "number", description: "Number of convolution filters" },
            ],
            returns: "2-D convolution layer object",
            examples: [
                { code: "layer = convolution2dLayer(3, 16)", result: "3×3 conv layer with 16 filters" },
            ],
            seeAlso: ["maxPooling2dLayer", "reluLayer"],
        },
        {
            name: "reluLayer",
            synopsis: "layer = reluLayer()",
            description: "Create a ReLU (rectified linear unit) activation layer. Applies max(0, x) elementwise.",
            returns: "ReLU activation layer object",
            examples: [
                { code: "layer = reluLayer()", result: "ReLU layer" },
            ],
            seeAlso: ["softmaxLayer", "fullyConnectedLayer"],
        },
        {
            name: "softmaxLayer",
            synopsis: "layer = softmaxLayer()",
            description: "Create a softmax activation layer. Normalizes outputs to probability distribution.",
            returns: "Softmax layer object",
            examples: [
                { code: "layer = softmaxLayer()", result: "Softmax layer" },
            ],
            seeAlso: ["classificationLayer", "fullyConnectedLayer"],
        },
        {
            name: "dlnetwork",
            synopsis: "net = dlnetwork(layers)",
            description: "Create a deep learning network from an array of layer definitions. The primary network container in modern MATLAB workflows.",
            parameters: [
                { name: "layers", type: "array", description: "Array of layer objects defining the network architecture" },
            ],
            returns: "Deep learning network object",
            examples: [
                { code: "net = dlnetwork([imageInputLayer([28 28 1]), fullyConnectedLayer(10), softmaxLayer()])", result: "Simple classification network" },
            ],
            seeAlso: ["trainnet", "predict", "classify"],
        },
        {
            name: "trainingOptions",
            synopsis: "options = trainingOptions(solver)",
            description: "Create training options for network training specifying the optimizer solver (sgdm, adam, rmsprop).",
            parameters: [
                { name: "solver", type: "string", description: "'sgdm', 'adam', or 'rmsprop'" },
            ],
            returns: "Training options object",
            examples: [
                { code: "opts = trainingOptions('adam')", result: "Adam optimizer options" },
            ],
            seeAlso: ["trainnet", "dlnetwork"],
        },
        {
            name: "trainnet",
            synopsis: "net = trainnet(data, layers, loss, options)",
            description: "Train a deep learning network using the specified data, architecture, loss function, and training options.",
            parameters: [
                { name: "data", type: "array", description: "Training data" },
                { name: "layers", type: "array", description: "Network layer array" },
                { name: "loss", type: "string", description: "Loss function name" },
                { name: "options", type: "object", description: "Training options from trainingOptions()" },
            ],
            returns: "Trained network object",
            examples: [
                { code: "net = trainnet(data, layers, 'crossentropy', opts)", result: "Trained network" },
            ],
            seeAlso: ["trainingOptions", "dlnetwork", "predict"],
        },
        {
            name: "predict",
            synopsis: "Y = predict(net, X)",
            description: "Run inference on a trained network. Returns predicted output values.",
            parameters: [
                { name: "net", type: "network", description: "Trained network" },
                { name: "X", type: "array", description: "Input data" },
            ],
            returns: "Predicted output array",
            seeAlso: ["classify", "trainnet"],
        },
        {
            name: "classify",
            synopsis: "labels = classify(net, X)",
            description: "Classify input data using a trained network. Returns predicted class labels.",
            parameters: [
                { name: "net", type: "network", description: "Trained classification network" },
                { name: "X", type: "array", description: "Input data" },
            ],
            returns: "Predicted class labels",
            seeAlso: ["predict", "trainnet"],
        },
    ],
    examples: [
        {
            title: "Build a Simple Network",
            description: "Create a basic classification network architecture",
            code: `% Define network layers
layers = [
  imageInputLayer([28 28 1])
  convolution2dLayer(3, 16)
  reluLayer()
  fullyConnectedLayer(10)
  softmaxLayer()
];
net = dlnetwork(layers)
disp(net)`,
        },
        {
            title: "Activation Functions",
            description: "Test ReLU and softmax activation functions",
            code: `% ReLU activation
x = [-2 -1 0 1 2];
y = relu(x)

% Softmax probabilities
scores = [2.0 1.0 0.1];
probs = softmax(scores)`,
        },
    ],
};
// ============================================================
// All Toolboxes Registry
// ============================================================
export const builtinToolboxes = [
    signalProcessingToolbox,
    communicationsToolbox,
    optimizationToolbox,
    statisticsToolbox,
    controlSystemsToolbox,
    linearAlgebraToolbox,
    imageProcessingToolbox,
    symbolicMathToolbox,
    deepLearningToolbox,
];
/**
 * Get a toolbox package by ID
 */
export function getToolbox(id) {
    return builtinToolboxes.find((t) => t.id === id);
}
/**
 * Get all toolbox IDs
 */
export function getToolboxIds() {
    return builtinToolboxes.map((t) => t.id);
}
/**
 * Get function documentation from any toolbox
 */
export function getToolboxFunctionDoc(funcName) {
    for (const tb of builtinToolboxes) {
        const fn = tb.functions.find((f) => f.name.toLowerCase() === funcName.toLowerCase());
        if (fn)
            return { ...fn, toolbox: tb.title };
    }
    return undefined;
}
/**
 * Check if a function name belongs to a built-in toolbox
 */
export function isToolboxFunction(funcName) {
    return builtinToolboxes.some((tb) => tb.builtinFunctions.includes(funcName));
}
//# sourceMappingURL=toolboxes.js.map